'use client';

import { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ContrastIcon from '@mui/icons-material/Contrast';
import CheckIcon from '@mui/icons-material/Check';
import { THEME_LABELS, THEME_NAMES, type ThemeName } from '@microservices/design-system';
import { useTheme } from '@/infrastructure/hooks/useTheme';

export function ThemeSelector() {
  const { themeName, setThemeName, isDark } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectTheme = (theme: ThemeName) => {
    setThemeName(theme);
    handleClose();
  };

  const getThemeIcon = (theme: ThemeName) => {
    if (theme.includes('high-contrast')) {
      return <ContrastIcon fontSize="small" />;
    }
    if (theme === 'dark') {
      return <Brightness4Icon fontSize="small" />;
    }
    return <Brightness7Icon fontSize="small" />;
  };

  return (
    <>
      <Tooltip title="Change theme">
        <IconButton
          onClick={handleClick}
          color="inherit"
          aria-controls={open ? 'theme-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          {isDark ? <Brightness4Icon /> : <Brightness7Icon />}
        </IconButton>
      </Tooltip>
      <Menu
        id="theme-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'theme-button',
        }}
      >
        {THEME_NAMES.map((theme) => (
          <MenuItem key={theme} onClick={() => handleSelectTheme(theme)} selected={themeName === theme}>
            <ListItemIcon>{getThemeIcon(theme)}</ListItemIcon>
            <ListItemText>{THEME_LABELS[theme]}</ListItemText>
            {themeName === theme && (
              <CheckIcon fontSize="small" sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
